import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../utils/api";

export interface AlertData {
  id: string;
  type: "info" | "warning" | "error" | "critical";
  title: string;
  message: string;
  category: "energy" | "weather" | "system" | "security" | "environmental";
  severity: 1 | 2 | 3 | 4 | 5; // 1=low, 5=critical
  timestamp: Date;
  location?: {
    lat: number;
    lng: number;
  };
  metadata?: {
    sensorId?: string;
    threshold?: number;
    currentValue?: number;
    unit?: string;
    imageUrl?: string;
    audioUrl?: string;
    videoUrl?: string;
  };
  acknowledged: boolean;
  dismissed: boolean;
  expiresAt?: Date;
}

export interface AlertPreferences {
  categories: string[];
  minSeverity: number;
  enableAudio: boolean;
  enableVisual: boolean;
  enablePush: boolean;
  quietHours: {
    enabled: boolean;
    start: string; // HH:MM
    end: string; // HH:MM
  };
}

export interface AlertState {
  alerts: AlertData[];
  preferences: AlertPreferences;
  loading: boolean;
  error: string | null;
  unreadCount: number;
  lastAlertId: string | null;
}

const defaultPreferences: AlertPreferences = {
  categories: ["energy", "weather", "system", "security", "environmental"],
  minSeverity: 2,
  enableAudio: true,
  enableVisual: true,
  enablePush: true,
  quietHours: {
    enabled: false,
    start: "22:00",
    end: "08:00",
  },
};

const initialState: AlertState = {
  alerts: [],
  preferences: defaultPreferences,
  loading: false,
  error: null,
  unreadCount: 0,
  lastAlertId: null,
};

// Async thunks
export const fetchAlerts = createAsyncThunk(
  "alerts/fetchAlerts",
  async (userId: string) => {
    const response = await api.get(`/api/alerts/${userId}`);
    if (response.status !== 200) {
      throw new Error("Failed to fetch alerts");
    }
    return response.data;
  },
);

export const acknowledgeAlert = createAsyncThunk(
  "alerts/acknowledge",
  async ({ alertId, userId }: { alertId: string; userId: string }) => {
    const response = await api.post(`/api/alerts/${alertId}/acknowledge`, {
      userId,
    });
    if (response.status !== 200) {
      throw new Error("Failed to acknowledge alert");
    }
    return response.data;
  },
);

export const dismissAlert = createAsyncThunk(
  "alerts/dismiss",
  async ({ alertId, userId }: { alertId: string; userId: string }) => {
    const response = await api.post(`/api/alerts/${alertId}/dismiss`, {
      userId,
    });
    if (response.status !== 200) {
      throw new Error("Failed to dismiss alert");
    }
    return response.data;
  },
);

const alertSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    addAlert: (state, action: PayloadAction<AlertData>) => {
      // Check if alert already exists
      const existingIndex = state.alerts.findIndex(
        (alert) => alert.id === action.payload.id,
      );
      if (existingIndex >= 0) {
        // Update existing alert
        state.alerts[existingIndex] = action.payload;
      } else {
        // Add new alert
        state.alerts.unshift(action.payload);
        state.lastAlertId = action.payload.id;
        state.unreadCount += 1;
      }

      // Keep only last 100 alerts
      if (state.alerts.length > 100) {
        state.alerts = state.alerts.slice(0, 100);
      }
    },

    updateAlert: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<AlertData> }>,
    ) => {
      const index = state.alerts.findIndex(
        (alert) => alert.id === action.payload.id,
      );
      if (index >= 0) {
        state.alerts[index] = {
          ...state.alerts[index],
          ...action.payload.updates,
        };
      }
    },

    removeAlert: (state, action: PayloadAction<string>) => {
      const index = state.alerts.findIndex(
        (alert) => alert.id === action.payload,
      );
      if (index >= 0) {
        const alert = state.alerts[index];
        if (!alert.acknowledged) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.alerts.splice(index, 1);
      }
    },

    acknowledgeAlertLocal: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert && !alert.acknowledged) {
        alert.acknowledged = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    dismissAlertLocal: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert && !alert.dismissed) {
        alert.dismissed = true;
        if (!alert.acknowledged) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },

    clearAllAlerts: (state) => {
      state.alerts = [];
      state.unreadCount = 0;
      state.lastAlertId = null;
    },

    updatePreferences: (
      state,
      action: PayloadAction<Partial<AlertPreferences>>,
    ) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },

    markAllAsRead: (state) => {
      state.alerts.forEach((alert) => {
        if (!alert.acknowledged) {
          alert.acknowledged = true;
        }
      });
      state.unreadCount = 0;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
        state.unreadCount = action.payload.filter(
          (alert: AlertData) => !alert.acknowledged,
        ).length;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch alerts";
      })
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        const alert = state.alerts.find((a) => a.id === action.payload.id);
        if (alert) {
          alert.acknowledged = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(dismissAlert.fulfilled, (state, action) => {
        const alert = state.alerts.find((a) => a.id === action.payload.id);
        if (alert) {
          alert.dismissed = true;
          if (!alert.acknowledged) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
  },
});

export const {
  addAlert,
  updateAlert,
  removeAlert,
  acknowledgeAlertLocal,
  dismissAlertLocal,
  clearAllAlerts,
  updatePreferences,
  markAllAsRead,
  clearError,
} = alertSlice.actions;

export default alertSlice.reducer;
